import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
} from "firebase/storage";
import { storage } from "./firebase"; // Importa la instancia correcta de Firebase Storage

// Comprime/redimensiona una imagen en el navegador antes de subirla.
// Lleva el lado mayor a `maxDim` px y la re-codifica como JPEG, bajando fotos
// de varios MB a unos cientos de KB (cargan mucho más rápido en las cards).
// Si algo falla (formato raro, no es imagen), devuelve el archivo original.
const compressImage = (file, { maxDim = 1600, quality = 0.8 } = {}) =>
  new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith("image/")) {
      return resolve(file);
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

// Sube varias imágenes y retorna sus URLs
export const saveMultipleImages = async (files, id) => {
  try {
    const urls = await Promise.all(
      files.map(async (file, index) => {
        // Crear una referencia única para cada archivo
        const storageRef = ref(storage, `/${id}/image_${index + 1}.jpeg`);

        // Comprimir/redimensionar antes de subir
        const optimized = await compressImage(file);

        // Subir el archivo (con contentType explícito)
        await uploadBytes(storageRef, optimized, { contentType: "image/jpeg" });

        // Obtener la URL de descarga
        const url = await getDownloadURL(storageRef);
        return url;
      })
    );
    return urls; // Array de URLs
  } catch (error) {
    console.error("Error al subir las imágenes:", error);
    throw error;
  }
};

// Función para eliminar una imagen del storage
export const deleteImageFromStorage = async (imageUrl) => {
  try {
    // Obtener la referencia al archivo en el storage
    const storageRef = ref(storage, imageUrl);

    // Eliminar el archivo
    await deleteObject(storageRef);

    console.log("Imagen eliminada con éxito del storage");
  } catch (error) {
    console.error("Error al eliminar la imagen del storage:", error);
    throw error;
  }
};

// Función para obtener las URLs de las imágenes basadas en un ID
export const getImagesById = async (id) => {
  try {
    // Referencia al directorio donde se encuentran las imágenes (basado en el ID)
    const folderRef = ref(storage, `/${id}`);

    // Obtener la lista de archivos en ese directorio
    const result = await listAll(folderRef);

    // Obtener las URLs de las imágenes
    const urls = await Promise.all(
      result.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        return url;
      })
    );

    return urls; // Array de URLs
  } catch (error) {
    console.error("Error al obtener las imágenes:", error);
    throw error;
  }
};
